import { API_BASE_URL } from "../../constants/index"; // Base URL for the API
import HttpInterface from "../../helpers/HttpInterface"; // Helper class for making HTTP requests
import { ArticleImage, ArticleImageDetail } from "../../Models/ArticleImage";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam"; // Fetch parameters model and default values
import { buildAPIUrl } from "../../api/apiHelpers"; // Utility function to build the API URL with query parameters

export class ArticleImagesService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests

  // Constructor initializes the httpProxy with the HttpInterface
  constructor() {
    this.httpProxy = new HttpInterface(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all article-images from the logistic service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing all article-images.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/logistic-service/article-images`, params),
      requestOptions
    );
  }

  /**
   * Fetches a specific articleImage by its ID from the API.
   *
   * @param id The unique identifier of the articleImage to fetch.
   * @returns A promise that resolves with the API response containing the articleImage details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific articleImage by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/article-images/${id}`,
      requestOptions
    );
  }

  /**
   * Creates a new articleImage in the logistic service API.
   *
   * @param articleImage The details of the articleImage to create.
   * @returns A promise that resolves with the API response confirming the creation of the article.
   */
  create(articleImage: ArticleImageDetail): Promise<any> {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(articleImage),
    };

    // Sends a POST request to create the new article
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/article-images`,
      requestOptions
    );
  }

  /**
   * Updates an existing articleImage in the logistic service API.
   *
   * @param articleImage The updated details of the article.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(articleImage: ArticleImage): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(articleImage), // Sends the updated articleImage data as JSON in the request body
    };

    // Sends a PUT request to update the specific articleImage by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/article-images/${articleImage.id}`,
      requestOptions
    );
  }

  /**
   * Deletes a articleImage from the logistic service API.
   *
   * @param id The unique identifier of the articleImage to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific articleImage by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/article-images/${id}`,
      requestOptions
    );
  }
}
